import MsgReader from '@kenjiuno/msgreader';
import type { FieldsData } from '@kenjiuno/msgreader';
import type { NormalizedAttachment, NormalizedEmail } from './parseEml';
import { classifyAttachment } from './parseEml';

function toUint8Array(x: unknown): Uint8Array {
  if (x instanceof Uint8Array) return x;
  if (x instanceof ArrayBuffer) return new Uint8Array(x);
  return new Uint8Array();
}

function normalizeMsgAttachments(reader: MsgReader, emailId: string, fields: FieldsData): NormalizedAttachment[] {
  const raw = fields.attachments || [];
  const out: NormalizedAttachment[] = [];
  raw.forEach((att, index) => {
    if (att.dataType !== 'attachment' || att.innerMsgContent) return;
    try {
      const filename =
        att.fileName?.trim() || att.fileNameShort?.trim() || att.name?.trim() || `attachment-${index + 1}`;
      const mime = att.attachMimeTag?.trim() || 'application/octet-stream';
      const bin = reader.getAttachment(att);
      const bytes = toUint8Array(bin.content);
      const { embeddable, reason } = classifyAttachment(filename, mime);
      out.push({
        id: `${emailId}-att-${out.length}`,
        filename,
        mimeType: mime,
        size: bytes.byteLength,
        content: bytes,
        disposition: 'attachment',
        contentId: att.pidContentId?.replace(/^<|>$/g, ''),
        related: false,
        embeddableInPdf: embeddable,
        unsupportedReason: embeddable ? undefined : reason,
      });
    } catch {
      /* skip broken attachment */
    }
  });
  return out;
}

function formatRecipients(rows: FieldsData[] | undefined, type: 'to' | 'cc'): string {
  if (!rows?.length) return '—';
  return rows
    .filter((r) => r.recipType === type)
    .map((r) => {
      const mail = r.email?.trim();
      const name = r.name?.trim();
      if (name && mail) return `${name} <${mail}>`;
      return mail || name || '—';
    })
    .filter(Boolean)
    .join(', ');
}

export function parseMsgToNormalizedEmail(buffer: ArrayBuffer, emailId: string, sourceFileName: string): NormalizedEmail {
  try {
    const reader = new MsgReader(buffer);
    const fields = reader.getFileData();
    if (fields.error) {
      throw new Error(fields.error);
    }
    if (fields.dataType !== 'msg') {
      throw new Error('Not an Outlook message');
    }

    const htmlBody =
      fields.bodyHtml?.trim() ||
      (fields.html instanceof Uint8Array ? new TextDecoder('utf-8', { fatal: false }).decode(fields.html) : null);

    const textBody = fields.body?.trim() || null;

    const attachments = normalizeMsgAttachments(reader, emailId, fields);

    const subject = fields.subject?.trim() || '(No subject)';
    const fromParts = [fields.senderName?.trim(), fields.senderEmail?.trim() || fields.senderSmtpAddress?.trim()].filter(
      Boolean,
    );
    const from = fromParts.length >= 2 ? `${fromParts[0]} <${fromParts[1]}>` : fromParts[0] || fromParts[1] || '—';

    return {
      id: emailId,
      sourceFileName,
      status: 'ready',
      isParseError: false,
      subject,
      from,
      to: formatRecipients(fields.recipients, 'to'),
      cc: formatRecipients(fields.recipients, 'cc'),
      date:
        fields.messageDeliveryTime?.trim() ||
        fields.clientSubmitTime?.trim() ||
        fields.creationTime?.trim() ||
        '—',
      textBody,
      htmlBody: htmlBody?.length ? htmlBody : null,
      attachments,
    };
  } catch (e) {
    const message = e instanceof Error ? e.message : 'Invalid .msg file';
    return {
      id: emailId,
      sourceFileName,
      status: 'error',
      isParseError: true,
      errorMessage: message,
      subject: '—',
      from: '—',
      to: '—',
      cc: '—',
      date: '—',
      textBody: null,
      htmlBody: null,
      attachments: [],
    };
  }
}
